import moment from 'moment';

/**
 * Get user information from localStorage
 * @returns {Object} User information and employment details
 */
export const getUserInfo = () => {
  const userStr = localStorage.getItem('user');
  const user = JSON.parse(userStr || '{}');
  const hairdresserInfo = user?.hairdresserInfo || {};
  const salonId = localStorage.getItem('salonId');
  
  // Convert employment type and salon owner status to numbers for comparison
  const employmentTypeId = parseInt(hairdresserInfo?.employmentTypeId || 0);
  const isSalonOwner = parseInt(hairdresserInfo?.isSalonOwner || 0);
  
  return {
    user,
    hairdresserInfo,
    salonId,
    employmentTypeId,
    isSalonOwner
  };
};

/**
 * Check if the user is a self-employed staff member (not salon owner)
 * @returns {Boolean} True if user is self-employed staff
 */
export const isUserSelfEmployed = () => {
  const { hairdresserInfo, employmentTypeId, isSalonOwner } = getUserInfo();
  return hairdresserInfo && employmentTypeId === 2 && isSalonOwner !== 1;
};

/**
 * Format date to display format
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted date string
 */
export const formatDate = (date) => {
  return moment(date).format('dddd, Do MMMM YYYY');
};

/**
 * Format time for last edit display
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted time string
 */
export const formatLastEditTime = (date) => {
  if (!date) return '';
  return moment(date).format('HH:mm [on] DD.MM.YY');
};

/**
 * Convert date from display format to API format
 * @param {String} date - Date in DD/MM/YYYY format
 * @returns {String} Date in YYYY-MM-DD format
 */
export const formatDateForApi = (date) => {
  return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
};

/**
 * Get default date range (current month)
 * @returns {Object} Object with startDate and endDate
 */
export const getDefaultDateRange = () => {
  return {
    startDate: moment().startOf('month').format('DD/MM/YYYY'),
    endDate: moment().endOf('month').format('DD/MM/YYYY')
  };
};

/**
 * Create stocktake parameters for API
 * @param {Object} pagination - Pagination parameters
 * @param {Object} dateRange - Date range parameters
 * @returns {Object} Parameters for stocktake API
 */
export const createStocktakeParams = (pagination, dateRange) => {
  const { salonId } = getUserInfo();
  
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    startDate: formatDateForApi(dateRange.startDate),
    endDate: formatDateForApi(dateRange.endDate),
    salonId: salonId
  };
  
  return params;
};

/**
 * Create new stocktake object
 * @param {String} name - Optional name for the stock take
 * @param {String} notes - Optional notes for the stock take
 * @returns {Object} New stocktake object for API
 */
export const createNewStocktakeObject = (name = '', notes = '') => {
  const { salonId, user } = getUserInfo();
  
  // Create stocktake with all required fields according to API model
  const newStocktake = {
    date: moment().format('YYYY-MM-DD'), // Date in ISO format
    salonId: salonId, // Salon ID is required
    userId: user._id, // User ID is required
    name: name || '', // No default name
    notes: notes || ''
  };
  
  return newStocktake;
};